import { useEffect, useRef, useState } from 'react';
import type { Stripe, StripeCardElement } from '@stripe/stripe-js';
import { api } from '../lib/api';
import { getStripe } from '../lib/stripe';
import { periodLabel, type ReactivationInfo } from './billingNotice';

const money = (cents: number, cur: string) =>
  `$${(cents / 100).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur.toUpperCase()}`;

interface Props {
  info: ReactivationInfo;
  onClose: () => void;
}

type ReactivateResult = { status: 'paid'; paid: number; voided: number } | { status: 'requires_action'; clientSecret: string };

/**
 * T4 (opción A de Arturo, 09/10) — "Pagar adeudo y reactivar" DENTRO de la app:
 * confirma la tarjeta guardada o captura otra (SetupIntent + confirmCardSetup, Stratos no
 * toca datos de tarjeta), y el backend la deja como predeterminada, cobra la factura vencida
 * y el periodo en curso, y anula los periodos que corrieron completos con la cuenta
 * suspendida. Stripe vuelve a `active` y el webhook reactiva al tenant: aquí se espera a
 * que /me lo refleje y se recarga.
 */
export function ReactivateDialog({ info, onClose }: Props) {
  const holder = useRef<HTMLDivElement>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [card, setCard] = useState<StripeCardElement | null>(null);
  const [useSaved, setUseSaved] = useState(!!info.card);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let el: StripeCardElement | null = null;
    getStripe().then((s) => {
      if (cancelled || !s) return;
      setStripe(s);
      if (!holder.current) return;
      el = s.elements().create('card', { hidePostalCode: true });
      el.mount(holder.current);
      setCard(el);
    });
    return () => { cancelled = true; try { el?.destroy(); } catch { /* ya desmontado */ } };
  }, []);

  /** El webhook de Stripe reactiva al tenant; se espera a que /me lo diga (≤ 40 s). */
  const waitActive = async () => {
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const me = await api<{ tenant?: { billing?: { suspendedAt: string | null } } }>('/me', { silentError: true });
        if (!me.tenant?.billing?.suspendedAt) { window.location.href = '/'; return; }
      } catch { /* sigue esperando */ }
    }
    setMsg('Pago recibido. Tu cuenta se reactivará en unos momentos; recarga la página si no cambia.');
  };

  const pay = async () => {
    if (!stripe) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      let pmId = useSaved ? info.card?.paymentMethodId ?? null : null;
      if (!pmId) {
        if (!card) throw new Error('Captura los datos de la tarjeta.');
        const { clientSecret } = await api<{ clientSecret: string }>('/billing/setup-intent', { method: 'POST', body: {} });
        const res = await stripe.confirmCardSetup(clientSecret, { payment_method: { card } });
        if (res.error) throw new Error(res.error.message ?? 'No se pudo guardar la tarjeta');
        const pm = res.setupIntent?.payment_method;
        pmId = typeof pm === 'string' ? pm : pm?.id ?? null;
        if (!pmId) throw new Error('Stripe no devolvió el método de pago');
      }
      // Hasta 3 vueltas: si el banco pide autenticación (3DS) se confirma y se reintenta;
      // el backend solo cobra lo que siga pendiente.
      for (let i = 0; i < 3; i++) {
        const r = await api<ReactivateResult>('/billing/reactivate', { method: 'POST', body: { paymentMethodId: pmId }, silentError: true });
        if (r.status === 'paid') {
          setMsg('Pago recibido. Reactivando tu cuenta…');
          await waitActive();
          return;
        }
        const auth = await stripe.confirmCardPayment(r.clientSecret);
        if (auth.error) throw new Error(auth.error.message ?? 'Tu banco no autorizó el cobro.');
      }
      throw new Error('No pudimos completar el cobro. Intenta de nuevo.');
    } catch (e: unknown) {
      const x = e as { body?: { message?: string }; message?: string };
      setErr(x?.body?.message || x?.message || 'No pudimos cobrar. Intenta con otra tarjeta.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 px-4" onClick={busy ? undefined : onClose}>
      <div
        className="card-soft max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-slate-200/80 bg-white p-6 text-left"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">Reactivar suscripción</p>
        <h2 className="mt-1 font-serif text-xl tracking-tight text-slate-900">Pagar adeudo y reactivar</h2>

        <div className="mt-4 space-y-2 rounded-xl border border-slate-200 p-4 text-sm text-slate-700">
          {info.toPay.map((i) => (
            <div key={i.id} className="flex justify-between gap-3">
              <span>Periodo {periodLabel(i.periodStart, i.periodEnd)}</span>
              <span className="font-medium">{money(i.amountDue, info.currency)}</span>
            </div>
          ))}
          <div className="flex justify-between border-t border-slate-100 pt-2 font-semibold text-slate-900">
            <span>Total a pagar</span>
            <span>{money(info.totalCents, info.currency)}</span>
          </div>
          {info.voided > 0 && (
            <p className="text-xs text-slate-500">
              {info.voided === 1 ? 'Un periodo' : `${info.voided} periodos`} en que tu cuenta estuvo suspendida no se cobra{info.voided === 1 ? '' : 'n'}.
            </p>
          )}
        </div>

        <div className="mt-4 space-y-2 text-sm">
          {info.card && (
            <label className="flex items-center gap-2">
              <input type="radio" checked={useSaved} onChange={() => setUseSaved(true)} />
              Usar mi tarjeta {info.card.brand.toUpperCase()} •••• {info.card.last4}
            </label>
          )}
          {info.card && (
            <label className="flex items-center gap-2">
              <input type="radio" checked={!useSaved} onChange={() => setUseSaved(false)} />
              Usar otra tarjeta
            </label>
          )}
          <div className={useSaved ? 'hidden' : ''}>
            <div ref={holder} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5" />
            <p className="mt-1 text-xs text-slate-400">Queda como tu tarjeta para los siguientes cobros.</p>
          </div>
        </div>

        {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}
        {msg && <p className="mt-3 text-sm text-emerald-700">{msg}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="rounded-xl px-4 py-2 text-sm text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={pay} disabled={busy || !stripe}
            className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
            {busy ? 'Procesando…' : `Pagar ${money(info.totalCents, info.currency)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
